import { Component } from '@theme/component';

const STORAGE_PREFIX = 'ossa-popup-';

/**
 * Site-wide configurable popup: banner or banner+discount, shown on a
 * configurable trigger (immediate / delay / scroll / exit-intent) with
 * configurable frequency capping (every visit / session / day / week).
 *
 * @extends Component
 */
class CustomPopupComponent extends Component {
  requiredRefs = ['panel', 'closeButton'];

  /** @type {number|undefined} */
  #timeoutId;
  /** @type {(() => void)|undefined} */
  #scrollHandler;
  /** @type {((event: MouseEvent) => void)|undefined} */
  #exitHandler;

  connectedCallback() {
    super.connectedCallback();

    if (this.#isSuppressed()) return;

    this.refs.closeButton.addEventListener('click', this.close);
    this.querySelector('.custom-popup__overlay')?.addEventListener('click', this.close);
    document.addEventListener('keydown', this.#handleKeydown);

    this.#bindTrigger();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#handleKeydown);
    this.#clearTriggers();
  }

  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.hasAttribute('open')) this.close();
  };

  open = () => {
    if (this.hasAttribute('open')) return;

    this.setAttribute('open', '');
    this.refs.panel.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => this.refs.closeButton.focus());
    this.#markShown();
    this.#clearTriggers();
  };

  close = () => {
    this.removeAttribute('open');
    this.refs.panel.setAttribute('aria-hidden', 'true');
  };

  #bindTrigger() {
    const trigger = this.dataset.trigger;
    const isTouch = !window.matchMedia('(pointer: fine)').matches;

    if (trigger === 'immediate') {
      this.#timeoutId = window.setTimeout(this.open, 300);
    } else if (trigger === 'delay') {
      const seconds = Number(this.dataset.delay) || 5;
      this.#timeoutId = window.setTimeout(this.open, seconds * 1000);
    } else if (trigger === 'scroll') {
      this.#bindScrollTrigger();
    } else if (trigger === 'exit_intent') {
      // Exit-intent has no equivalent on touch devices, fall back to scroll.
      if (isTouch) {
        this.#bindScrollTrigger();
      } else {
        this.#exitHandler = (event) => {
          if (event.clientY <= 0) this.open();
        };
        document.addEventListener('mouseout', this.#exitHandler);
      }
    }
  }

  #bindScrollTrigger() {
    const threshold = Number(this.dataset.scroll) || 50;
    this.#scrollHandler = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 100;
      if (scrolled >= threshold) this.open();
    };
    document.addEventListener('scroll', this.#scrollHandler, { passive: true });
  }

  #clearTriggers() {
    if (this.#timeoutId) window.clearTimeout(this.#timeoutId);
    if (this.#scrollHandler) document.removeEventListener('scroll', this.#scrollHandler);
    if (this.#exitHandler) document.removeEventListener('mouseout', this.#exitHandler);
  }

  get #storageKey() {
    return `${STORAGE_PREFIX}${this.dataset.storageKey}`;
  }

  #isSuppressed() {
    const frequency = this.dataset.frequency;
    if (!frequency || frequency === 'every_visit') return false;

    if (frequency === 'once_per_session') {
      return sessionStorage.getItem(this.#storageKey) === 'shown';
    }

    const lastShown = Number(localStorage.getItem(this.#storageKey) || 0);
    if (!lastShown) return false;

    const elapsedHours = (Date.now() - lastShown) / 36e5;
    if (frequency === 'once_per_day') return elapsedHours < 24;
    if (frequency === 'once_per_week') return elapsedHours < 24 * 7;

    return false;
  }

  #markShown() {
    const frequency = this.dataset.frequency;
    if (frequency === 'once_per_session') {
      sessionStorage.setItem(this.#storageKey, 'shown');
    } else if (frequency === 'once_per_day' || frequency === 'once_per_week') {
      localStorage.setItem(this.#storageKey, String(Date.now()));
    }
  }
}

if (!customElements.get('custom-popup-component')) {
  customElements.define('custom-popup-component', CustomPopupComponent);
}
